import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import type { Dayjs } from 'dayjs';

import { GET_SCOPED_PRODUCT_SALES_FOR_PRODUCTS } from '../apollo/queries/productSalesBulk';
import { getLastCompletedFinancialWeekSpan } from '../helpers/financialWeek';
import { useAppConfig } from './useAppConfig';
import { useSalesDataMaintenance } from './useSalesDataMaintenance';
import type { ProductSalesScopeInput } from './useProductSalesScope';

const WARM_REFETCH_DELAY_MS = 4000;
const WARM_REFETCH_MAX_ATTEMPTS = 3;
const EMPTY_PRODUCT_IDS = new Set<string>();

export type ProductSalesForSurveyItems = {
    thisYear: Record<string, number>;
    lastYear: Record<string, number>;
    loading: boolean;
    isWarming: boolean;
    warmingProductIds: Set<string>;
    maintenanceProductIds: Set<string>;
    warmTimedOut: boolean;
    refetch: () => Promise<void>;
};

type ProductSalesTotals = {
    productId: string;
    totalQuantity: number;
};

type ScopedBulkProductSalesQueryData = {
    getScopedProductSalesForProducts: {
        items: ProductSalesTotals[];
        isWarming: boolean;
        warmingProductIds: string[];
        storeCount: number;
    };
};

type BulkProductSalesQueryVars = {
    productIds: string[];
    startDate: Date;
    endDate: Date;
    scope?: ProductSalesScopeInput;
};

function toQuantityMap(items: ProductSalesTotals[] | undefined) {
    const map: Record<string, number> = {};
    items?.forEach(item => {
        map[item.productId] = item.totalQuantity ?? 0;
    });
    return map;
}

export function useProductSalesForSurveyItems(
    productIds: string[],
    referenceDate: Date | Dayjs | string,
    scopeInput?: ProductSalesScopeInput,
): ProductSalesForSurveyItems {
    const { financialYearStartMonth } = useAppConfig();
    const salesMaintenance = useSalesDataMaintenance();
    const { startDate, endDate, lastYearStartDate, lastYearEndDate } =
        useMemo(() => {
            const anchor =
                typeof referenceDate === 'string' ||
                referenceDate instanceof Date
                    ? referenceDate
                    : referenceDate.toDate();
            const span = getLastCompletedFinancialWeekSpan(
                anchor,
                financialYearStartMonth,
            );
            return {
                startDate: span.thisYear.startDate,
                endDate: span.thisYear.endDate,
                lastYearStartDate: span.lastYear.startDate,
                lastYearEndDate: span.lastYear.endDate,
            };
        }, [referenceDate, financialYearStartMonth]);

    const skip = productIds.length === 0;

    const {
        data: thisYearData,
        loading: thisYearLoading,
        refetch: refetchThisYear,
    } = useQuery<ScopedBulkProductSalesQueryData, BulkProductSalesQueryVars>(
        GET_SCOPED_PRODUCT_SALES_FOR_PRODUCTS,
        {
            skip,
            fetchPolicy: 'cache-first',
            variables: {
                productIds,
                startDate,
                endDate,
                scope: scopeInput,
            },
        },
    );

    const {
        data: lastYearData,
        loading: lastYearLoading,
        refetch: refetchLastYear,
    } = useQuery<ScopedBulkProductSalesQueryData, BulkProductSalesQueryVars>(
        GET_SCOPED_PRODUCT_SALES_FOR_PRODUCTS,
        {
            skip,
            fetchPolicy: 'cache-first',
            variables: {
                productIds,
                startDate: lastYearStartDate,
                endDate: lastYearEndDate,
                scope: scopeInput,
            },
        },
    );

    const thisYear = useMemo(
        () =>
            toQuantityMap(thisYearData?.getScopedProductSalesForProducts.items),
        [thisYearData],
    );

    const lastYear = useMemo(
        () =>
            toQuantityMap(lastYearData?.getScopedProductSalesForProducts.items),
        [lastYearData],
    );

    const isWarming = Boolean(
        thisYearData?.getScopedProductSalesForProducts.isWarming ||
            lastYearData?.getScopedProductSalesForProducts.isWarming,
    );

    const warmingProductIds = useMemo(() => {
        return new Set([
            ...(thisYearData?.getScopedProductSalesForProducts
                .warmingProductIds ?? []),
            ...(lastYearData?.getScopedProductSalesForProducts
                .warmingProductIds ?? []),
        ]);
    }, [thisYearData, lastYearData]);

    const warmKey = [
        productIds.join(','),
        startDate.toISOString(),
        lastYearStartDate.toISOString(),
        scopeInput?.advertisingZoneId ?? '',
        (scopeInput?.storeIds ?? []).join(','),
    ].join('|');
    const [warmAttempts, setWarmAttempts] = useState(0);

    useEffect(() => {
        setWarmAttempts(0);
    }, [warmKey]);

    useEffect(() => {
        if (salesMaintenance.isActive || !isWarming) {
            return undefined;
        }
        if (warmAttempts >= WARM_REFETCH_MAX_ATTEMPTS) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setWarmAttempts(current => current + 1);
            refetchThisYear().catch(() => undefined);
            refetchLastYear().catch(() => undefined);
        }, WARM_REFETCH_DELAY_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        salesMaintenance.isActive,
        isWarming,
        warmKey,
        warmAttempts,
        refetchThisYear,
        refetchLastYear,
    ]);

    const loading = thisYearLoading || lastYearLoading;

    const warmTimedOut =
        !salesMaintenance.isActive &&
        isWarming &&
        warmAttempts >= WARM_REFETCH_MAX_ATTEMPTS;

    const maintenanceProductIds = salesMaintenance.isActive
        ? warmingProductIds
        : EMPTY_PRODUCT_IDS;

    return {
        thisYear,
        lastYear,
        loading,
        isWarming,
        warmingProductIds,
        maintenanceProductIds,
        warmTimedOut,
        refetch: async () => {
            await Promise.all([
                refetchThisYear().catch(() => undefined),
                refetchLastYear().catch(() => undefined),
            ]);
        },
    };
}
